import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CalendarClockIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { portalMeetingPollsApi } from '@/api/meeting-polls'
import type { PortalMeetingPoll } from '@/api/types'
import { useCurrentUser } from '@/auth/use-auth'
import { PortalBookings } from '@/components/booking/portal-bookings'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { portalMeetingState, slotLabel } from '@/lib/meeting-poll'

/** Espace client › Rendez-vous (lot 26) : les demandes où vous êtes invité. */
export const Route = createFileRoute('/portal/rendez-vous/')({
  component: PortalMeetingPollsPage,
})

const STATE_VARIANTS = {
  todo: 'brand',
  answered: 'muted',
  decided: 'success',
  cancelled: 'muted',
} as const

function PortalMeetingPollsPage() {
  const viewer = useCurrentUser()
  const t = msg().portalScheduling.polls
  const polls = useQuery({
    queryKey: ['portal', 'meeting-polls'],
    queryFn: () => portalMeetingPollsApi.list(),
  })
  const items = [...(polls.data ?? [])].sort(
    (a, b) => Number(portalMeetingState(b) === 'todo') - Number(portalMeetingState(a) === 'todo'),
  )
  return (
    <div className="space-y-6">
      <PageHeader title={t.title} documentTitle={t.title} />
      <PortalBookings />
      <Frame>
        <h2 className="font-semibold tracking-tight">{t.requestsTitle}</h2>
        {polls.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : polls.isError ? (
          <FormError>
            {polls.error instanceof ApiError ? polls.error.message : t.unreadable}
          </FormError>
        ) : items.length === 0 ? (
          <EmptyState
            icon={CalendarClockIcon}
            title={t.emptyTitle}
            description={t.emptyDescription}
          />
        ) : (
          <ul className="divide-y" data-testid="rendez-vous-portail">
            {items.map((poll: PortalMeetingPoll) => {
              const state = portalMeetingState(poll)
              return (
                <li key={poll.id} className="flex flex-wrap items-center gap-2 py-3">
                  <Link
                    to="/portal/rendez-vous/$pollId"
                    params={{ pollId: poll.id }}
                    className="min-w-0 flex-1 font-medium hover:underline"
                  >
                    {poll.title}
                  </Link>
                  <Badge variant={STATE_VARIANTS[state]}>{t.states[state]}</Badge>
                  {poll.decided_slot && (
                    <span className="basis-full text-sm text-muted-foreground first-letter:uppercase">
                      {slotLabel(poll.decided_slot, viewer.timezone)}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </Frame>
    </div>
  )
}
